import React, { lazy, Suspense } from 'react'
import PropTypes from 'prop-types'
import { css } from '@linaria/core'
import { useTranslation } from 'react-i18next'
import { useQueryParams } from '@fs/zion-router'
import { useOverlay, Bleedable, FlowGrid, Separator } from '@fs/zion-ui'
import { MemoryCollage } from '@fs/zion-memory-card'
import { trackEvent } from '@fs/zion-analytics'
import InfoCard from '../info-card/PersonInfoCard'
import StoryCardBody from './StoryCardBody'

const MediaOverlay = lazy(() => import('../media-overlay/MediaOverlay'))

const storyCss = css`
  cursor: pointer;
`

export default function StoryCard({ data, person, loggedIn = false, restricted = false, baseUrl, ...props }) {
  const [t] = useTranslation()
  const { query } = useQueryParams()
  const overlay = useOverlay({ initiallyOpen: query?.memoryUrl?.includes(data?.url) })

  function onMemoryClick() {
    trackEvent({ link_name: 'Story: Memory' })
    overlay.handleClick()
  }

  return (
    <FlowGrid columnCount={1}>
      <InfoCard heading={t('info-cards.story-card.header')} {...props}>
        <div
          onClick={onMemoryClick}
          role="button"
          tabIndex="0"
          aria-label={t('info-cards.view-all.action')}
          className={storyCss}
          onKeyDown={(e) => {
            if (/(^Enter$)|(^NumpadEnter$)/.test(e.key)) onMemoryClick()
          }}
          data-testid="story-card-click"
        >
          {!!data?.associatedMedia?.length && (
            <Bleedable sides={0} top={0} bottom={0}>
              <MemoryCollage images={data.associatedMedia.map((image) => image.url)} height="200px" />
            </Bleedable>
          )}
          <Separator size="nano" />
          <StoryCardBody data={data} person={person} />
        </div>
        {overlay.isOpen && (
          <Suspense fallback="">
            <MediaOverlay
              {...overlay}
              title={person?.name}
              mediaType="memory"
              person={person}
              media={[data]}
              selectedMedia={data}
              baseUrl={baseUrl}
              loggedIn={loggedIn}
              restricted={restricted}
            />
          </Suspense>
        )}
      </InfoCard>
    </FlowGrid>
  )
}

// Stryker disable all
StoryCard.propTypes = {
  /** Data to display for StoryCard including the images and description */
  data: PropTypes.shape({}),

  /** Person object for the person who's data is being displayed. */
  person: PropTypes.shape({}),
}
